import { createSignal, createEffect } from 'solid-js';
import { createStore } from 'solid-js/store';
import type {
  Team,
  Game,
  Answer,
  TeamProgress,
  RankingEntry,
  DashboardData,
  LoadingState,
  ErrorState,
  GameTemplate,
  GameInstance,
  SessionParticipation,
} from '@/types';
import { apiService } from '@/services/api';
import { WebSocketService, createGameWebSocket, createTeamWebSocket } from '@/services/websocket';

// Main game state store (supports both legacy and new architecture)
const [gameState, setGameState] = createStore<{
  isAuthenticated: boolean;
  isAdmin: boolean;
  team: Team | null;
  game: Game | null;
  answers: Answer[];
  progress: TeamProgress[];
  ranking: RankingEntry[];
  // New architecture fields
  architecture: 'legacy' | 'new';
  session: GameInstance | null;
  template: GameTemplate | null;
  participation: SessionParticipation | null;
  // Phone verification
  phoneVerified: boolean;
  phoneNumber: string | null;
  // Ranking settings
  rankingUpdateFrequency: number;
  rankingFreezeTime: number;
  rankingFrozen: boolean;
  nextRankingUpdate: number;
  teamFinished: boolean;
  // Chat settings
  chatEnabled: boolean;
}>({
  isAuthenticated: false,
  isAdmin: false,
  team: null,
  game: null,
  answers: [],
  progress: [],
  ranking: [],
  // New architecture defaults
  architecture: 'legacy',
  session: null,
  template: null,
  participation: null,
  // Phone verification defaults
  phoneVerified: true,  // Default to true for legacy architecture
  phoneNumber: null,
  // Ranking defaults
  rankingUpdateFrequency: 15,
  rankingFreezeTime: 30,
  rankingFrozen: false,
  nextRankingUpdate: 0,
  teamFinished: false,
  // Chat defaults
  chatEnabled: true,
});

// Loading states
const [loadingState, setLoadingState] = createStore<LoadingState>({
  login: false,
  dashboard: false,
  submission: false,
  ranking: false,
});

// Error states
const [errorState, setErrorState] = createStore<ErrorState>({});

// UI state signals
const [timeRemaining, setTimeRemaining] = createSignal<number>(0);
const [isGameActive, setIsGameActive] = createSignal<boolean>(false);

// Session management
const [heartbeatInterval, setHeartbeatInterval] = createSignal<number | null>(null);

// WebSocket management
const [gameWebSocket, setGameWebSocket] = createSignal<WebSocketService | null>(null);
const [teamWebSocket, setTeamWebSocket] = createSignal<WebSocketService | null>(null);
const [isConnected, setIsConnected] = createSignal<boolean>(false);

// Notification system
const [notification, setNotification] = createSignal<{
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  duration?: number;
} | null>(null);

// Collected digits for game completion
const [collectedDigits, setCollectedDigits] = createStore<Array<{
  tile_number: number;
  digit: string;
  points: number;
}>>([]);

// Game completion state
const [isGameCompleted, setIsGameCompleted] = createSignal<boolean>(false);

// Initialize store from localStorage on startup
const initializeStore = async () => {
  // Check for QR login parameter (added by backend after QR scan)
  const urlParams = new URLSearchParams(window.location.search);
  const isQrLogin = urlParams.get('qr') === '1';

  // Clean up URL after reading QR parameter (remove ?qr=1 from URL bar)
  if (isQrLogin && window.history.replaceState) {
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, document.title, cleanUrl);
  }

  const savedSession = localStorage.getItem('game_session');

  // Try to restore session if we have localStorage OR if this is a QR login
  if (savedSession || isQrLogin) {
    try {
      // Try to restore session by loading dashboard
      // This will work if the session cookie is still valid
      await loadDashboard();
      setGameState('isAuthenticated', true);
      startHeartbeat();

      // For QR login, save session to localStorage for future refreshes
      if (isQrLogin && !savedSession) {
        const teamName = gameState.team?.name || 'QR Login';
        localStorage.setItem('game_session', JSON.stringify({
          teamName: teamName,
          timestamp: Date.now(),
          loginMethod: 'qr'
        }));
      }

      // WebSocket connection will be established after successful dashboard load
    } catch (error) {
      // Session expired or invalid - clear saved data
      console.log('Session expired, clearing stored data');
      localStorage.removeItem('game_session');
      localStorage.removeItem('auth_token');
    }
  }
};

// Authentication actions
const login = async (teamName: string, password: string = '') => {
  setLoadingState('login', true);
  setErrorState('login', undefined);

  try {
    const response = await apiService.login({ team_name: teamName, password });

    if (response.success) {
      // Determine architecture from response
      const isNewArch = response.architecture === 'new' || response.session != null;

      setGameState({
        isAuthenticated: true,
        isAdmin: false,
        team: response.team,
        game: response.game,
        // New architecture fields
        architecture: isNewArch ? 'new' : 'legacy',
        session: response.session || null,
        template: response.template || null,
        participation: response.participation || null,
        // Phone verification - only required for new architecture
        phoneVerified: isNewArch ? (response.phone_verified ?? false) : true,
        phoneNumber: response.phone_number || null,
      });

      // Save session indicator to persist across page refresh
      localStorage.setItem('game_session', JSON.stringify({
        teamName: response.team.name,
        timestamp: Date.now()
      }));

      await loadDashboard();
      startHeartbeat();
      connectWebSockets();
    } else {
      throw new Error('Login failed');
    }
  } catch (error: any) {
    setErrorState('login', error.message);
    throw error;
  } finally {
    setLoadingState('login', false);
  }
};

// Admin authentication
const adminLogin = async (username: string, password: string) => {
  setLoadingState('login', true);
  setErrorState('login', undefined);

  try {
    // For now, simple admin check - in production would use proper Django admin auth
    if (username === 'admin' && password === 'admin123') {
      setGameState({
        isAuthenticated: true,
        isAdmin: true,
        team: null,
        game: null,
      });
      startHeartbeat();
    } else {
      throw new Error('Invalid admin credentials');
    }
  } catch (error: any) {
    setErrorState('login', error.message);
    throw error;
  } finally {
    setLoadingState('login', false);
  }
};

const logout = async () => {
  try {
    await apiService.logout();
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    stopHeartbeat();
    disconnectWebSockets();
    setGameState({
      isAuthenticated: false,
      isAdmin: false,
      team: null,
      game: null,
      answers: [],
      progress: [],
      ranking: [],
      // Clear new architecture fields
      architecture: 'legacy',
      session: null,
      template: null,
      participation: null,
      // Clear phone verification
      phoneVerified: true,
      phoneNumber: null,
    });
    setTimeRemaining(0);
    setIsGameActive(false);
    setIsGameCompleted(false);
    setCollectedDigits([]);
    clearAllErrors();
  }
};

// Dashboard data loading
const loadDashboard = async () => {
  setLoadingState('dashboard', true);
  setErrorState('dashboard', undefined);

  try {
    const data: DashboardData = await apiService.getDashboard();

    // Determine architecture from response
    const isNewArch = data.architecture === 'new' || data.session != null;

    setGameState({
      team: data.team,
      game: data.game,
      answers: data.answers,
      progress: data.progress,
      ranking: data.current_ranking,
      // New architecture fields
      architecture: isNewArch ? 'new' : 'legacy',
      session: data.session || null,
      template: data.template || null,
      participation: data.participation || null,
      // Phone verification - required for SMS
      phoneVerified: isNewArch ? (data.phone_verified ?? false) : true,
      phoneNumber: data.phone_number || null,
      // Ranking settings
      rankingUpdateFrequency: data.ranking_update_frequency ?? 15,
      rankingFreezeTime: data.ranking_freeze_time ?? 30,
      rankingFrozen: data.ranking_frozen ?? false,
      nextRankingUpdate: data.next_ranking_update ?? 0,
      teamFinished: data.team_finished ?? false,
      // Chat settings
      chatEnabled: data.chat_enabled ?? true,
    });

    // Update time remaining and game status based on architecture
    if (isNewArch && data.session) {
      // New architecture: use session status
      if (data.session.time_remaining) {
        setTimeRemaining(parseTimeRemaining(data.session.time_remaining));
      }
      setIsGameActive(data.session.status === 'active');
    } else if (data.game) {
      // Legacy architecture
      if (data.game.time_remaining) {
        setTimeRemaining(parseTimeRemaining(data.game.time_remaining));
      }
      setIsGameActive(data.game.status === 'active');
    }

    // Establish WebSocket connections after successful dashboard load
    connectWebSockets();
  } catch (error: any) {
    setErrorState('dashboard', error.message);
    throw error;
  } finally {
    setLoadingState('dashboard', false);
  }
};

// Answer submission
const submitAnswer = async (tileNumber: number, answerText: string) => {
  setLoadingState('submission', true);
  setErrorState('submission', undefined);

  try {
    const response = await apiService.submitAnswer({
      tile_number: tileNumber,
      answer_text: answerText,
    });

    if (response.success) {
      // Update team points
      if (gameState.team) {
        setGameState('team', 'total_points', response.total_points);
      }

      // Refresh dashboard to get updated progress
      await loadDashboard();
    }

    return response;
  } catch (error: any) {
    setErrorState('submission', error.message);
    throw error;
  } finally {
    setLoadingState('submission', false);
  }
};

// Ranking updates
const updateRanking = async () => {
  if (!gameState.game) return;

  setLoadingState('ranking', true);
  setErrorState('ranking', undefined);

  try {
    const ranking = await apiService.getRanking(gameState.game.id);
    setGameState('ranking', ranking);
  } catch (error: any) {
    setErrorState('ranking', error.message);
  } finally {
    setLoadingState('ranking', false);
  }
};

// Parse time_remaining string (HH:MM:SS or HH:MM:SS.microseconds) to seconds
const parseTimeRemaining = (timeStr: string | number | null | undefined): number => {
  if (timeStr === null || timeStr === undefined) return 0;
  if (typeof timeStr === 'number') return timeStr;
  if (typeof timeStr !== 'string') return 0;

  // Handle format "HH:MM:SS" or "HH:MM:SS.microseconds"
  const parts = timeStr.split(':');
  if (parts.length >= 3) {
    const hours = parseInt(parts[0], 10) || 0;
    const minutes = parseInt(parts[1], 10) || 0;
    const seconds = parseFloat(parts[2]) || 0;
    return hours * 3600 + minutes * 60 + Math.floor(seconds);
  }

  // Try parsing as number (backward compatibility)
  const num = parseFloat(timeStr);
  return isNaN(num) ? 0 : num;
};

// Game timer management
const startGameTimer = () => {
  const timer = setInterval(() => {
    const current = timeRemaining();
    if (current > 0) {
      setTimeRemaining(current - 1);
    } else {
      setIsGameActive(false);
      clearInterval(timer);
    }
  }, 1000);

  return timer;
};

// Heartbeat for session management
const startHeartbeat = () => {
  const interval = setInterval(async () => {
    try {
      await apiService.heartbeat();
    } catch (error) {
      console.error('Heartbeat failed:', error);
      // Optionally handle session expiration
    }
  }, 60000); // Every minute

  setHeartbeatInterval(interval as any);
};

const stopHeartbeat = () => {
  const interval = heartbeatInterval();
  if (interval) {
    clearInterval(interval);
    setHeartbeatInterval(null);
  }
};

// Stop game
const stopGame = async () => {
  try {
    await apiService.stopGame();
    setIsGameActive(false);
    await loadDashboard(); // Refresh to get updated state
  } catch (error: any) {
    setErrorState('general', error.message);
    throw error;
  }
};

// Phone verification
const verifyPhone = async (phoneNumber: string) => {
  setLoadingState('submission', true);
  setErrorState('phone', undefined);

  try {
    const response = await apiService.verifyPhone(phoneNumber);

    if (response.success) {
      setGameState({
        phoneVerified: true,
        phoneNumber: response.phone_number || phoneNumber,
      });
      return { success: true };
    } else {
      setErrorState('phone', response.error);
      return { success: false, error: response.error };
    }
  } catch (error: any) {
    setErrorState('phone', error.message);
    return { success: false, error: error.message };
  } finally {
    setLoadingState('submission', false);
  }
};

// Error management
const clearError = (key: keyof ErrorState) => {
  setErrorState(key, undefined);
};

const clearAllErrors = () => {
  setErrorState({});
};

// Auto-refresh effects
createEffect(() => {
  if (gameState.isAuthenticated && isGameActive()) {
    const timer = startGameTimer();
    
    // Auto-refresh ranking every 30 seconds during active game
    const rankingRefresh = setInterval(() => {
      updateRanking();
    }, 30000);

    return () => {
      clearInterval(timer);
      clearInterval(rankingRefresh);
    };
  }
});

// Initialize store on module load
initializeStore();

// WebSocket connection management
const connectWebSockets = () => {
  if (!gameState.game || !gameState.team) return;
  
  // Connect to game WebSocket
  const gameWS = createGameWebSocket(gameState.game.id);
  gameWS.connect();
  setGameWebSocket(gameWS);
  
  // Connect to team WebSocket
  const teamWS = createTeamWebSocket(gameState.team.id);
  teamWS.connect();
  setTeamWebSocket(teamWS);
  
  console.log('WebSocket connections established');
};

const disconnectWebSockets = () => {
  const gameWS = gameWebSocket();
  const teamWS = teamWebSocket();
  
  if (gameWS) {
    gameWS.disconnect();
    setGameWebSocket(null);
  }
  
  if (teamWS) {
    teamWS.disconnect();
    setTeamWebSocket(null);
  }
  
  setIsConnected(false);
  console.log('WebSocket connections closed');
};

// Real-time update handlers
const updateGame = (gameData: Partial<Game>) => {
  setGameState('game', (prev) => ({ ...prev, ...gameData }));

  if (gameData.time_remaining !== undefined) {
    setTimeRemaining(parseTimeRemaining(gameData.time_remaining));
  }

  if (gameData.status) {
    setIsGameActive(gameData.status === 'active');
  }
};

const updateTeam = (teamData: Partial<Team>) => {
  setGameState('team', (prev) => ({ ...prev, ...teamData }));
};

// Update session (new architecture) - used for real-time session status updates
const updateSession = (sessionData: Partial<GameInstance>) => {
  setGameState('session', (prev) => prev ? { ...prev, ...sessionData } : null);

  // Update game active state based on session status
  if (sessionData.status) {
    setIsGameActive(sessionData.status === 'active');
  }

  // Update time remaining if provided
  if (sessionData.time_remaining !== undefined) {
    setTimeRemaining(parseTimeRemaining(sessionData.time_remaining));
  }
};

const updateRankingFromWS = (rankingData: RankingEntry[]) => {
  setGameState('ranking', rankingData);
};

const addCollectedDigit = (digit: { tile_number: number; digit: string; points: number }) => {
  setCollectedDigits((prev) => [...prev.filter(d => d.tile_number !== digit.tile_number), digit]);
};

const setConnectionStatus = (connected: boolean) => {
  setIsConnected(connected);
};

const setGameCompleted = (completed: boolean) => {
  setIsGameCompleted(completed);
};

// Auto-clear notifications
createEffect(() => {
  const notif = notification();
  if (notif && notif.duration) {
    setTimeout(() => {
      setNotification(null);
    }, notif.duration);
  }
});

// Cleanup WebSockets on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    disconnectWebSockets();
  });
}

// Export store and actions
export const gameStore = {
  // State
  gameState,
  loadingState,
  errorState,
  timeRemaining,
  isGameActive,
  isConnected,
  notification,
  collectedDigits,
  isGameCompleted,
  
  // Actions
  login,
  adminLogin,
  logout,
  loadDashboard,
  submitAnswer,
  updateRanking,
  stopGame,
  verifyPhone,
  clearError,
  clearAllErrors,
  
  // WebSocket actions
  connectWebSockets,
  disconnectWebSockets,
  updateGame,
  updateTeam,
  updateSession,
  updateRankingFromWS,
  addCollectedDigit,
  setConnectionStatus,
  setNotification,
  setGameCompleted,
};

// Legacy exports for backward compatibility
export {
  gameState,
};